import { useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import {
  getIntegrationsApi,
  resolveIntegrationsWorkspaceId,
  type MiroBoard,
  type MiroImportPreview,
  type MiroTemplate,
  type MiroTemplateImportPreview,
  type MiroTemplateExportPreview,
} from '../../../../lib/integrationsApi';
import {
  saveWorkspaceConnectorCredentials,
  clearConnectorCredentials,
  enableWorkspaceConnector,
} from '../../../../lib/connectorConfigsService';
import { getCurrentWorkspaceId } from '../../../../lib/supabaseSync';
import type { MiroColorRule } from '../../../../lib/fieldMappingRegistry';
import { summarizeImportOutcome, type ImportSaveOutcome } from '../../../../lib/importBatch';
import {
  miroStickyColorsForExport,
  prepareMiroImportMapping,
  type MiroImportMapping,
} from '../../../../lib/miroImportMapping';
import type {
  ImportItemPayload,
  MiroStickyNote,
  PriorityExportRecord,
  SyncStatus,
} from './integrationTypes';

type Notify = (notification: {
  type: 'success' | 'warning' | 'error' | 'info';
  title: string;
  message: string;
  duration?: number;
}) => void;

/** Miro connection, credentials, board/template data, and import/export handlers. */
export function useMiroConnection({
  api,
  workspaceId,
  listId,
  listName,
  priorities,
  onImportItems,
  onNotification,
  itemColorRules,
  setSyncStatus,
}: {
  api: ReturnType<typeof getIntegrationsApi>;
  workspaceId: ReturnType<typeof resolveIntegrationsWorkspaceId>;
  listId: string;
  listName: string;
  priorities: PriorityExportRecord[];
  onImportItems: (items: ImportItemPayload[]) => Promise<ImportSaveOutcome>;
  onNotification: Notify;
  itemColorRules: MiroColorRule[];
  setSyncStatus: Dispatch<SetStateAction<SyncStatus>>;
}) {
  // Connection state
  const [miroConnected, setMiroConnected] = useState(false);
  const [showMiroConfig, setShowMiroConfig] = useState(false);

  // Connection credentials (load from localStorage)
  const [miroAccessToken, setMiroAccessToken] = useState(() => {
    try {
      return localStorage.getItem('peak_miro_token') || '';
    } catch {
      return '';
    }
  });
  const [miroTeamId, setMiroTeamId] = useState(() => {
    try {
      return localStorage.getItem('peak_miro_team_id') || '';
    } catch {
      return '';
    }
  });

  // Data from APIs
  const [miroBoards, setMiroBoards] = useState<MiroBoard[]>([]);
  const [selectedMiroBoard, setSelectedMiroBoard] = useState<string>('');
  const [, setMiroPreview] = useState<MiroImportPreview | null>(null);
  const [miroItems, setMiroItems] = useState<MiroStickyNote[]>([]);

  // Miro Template states
  const [miroTemplates, setMiroTemplates] = useState<MiroTemplate[]>([]);
  const [selectedMiroTemplate, setSelectedMiroTemplate] = useState<string>('');
  const [useTemplateMode, setUseTemplateMode] = useState(true);
  const [templateImportPreview, setTemplateImportPreview] = useState<MiroTemplateImportPreview | null>(null);
  const [templateExportPreview, setTemplateExportPreview] = useState<MiroTemplateExportPreview | null>(null);

  // Modal states
  const [showImportMiro, setShowImportMiro] = useState(false);
  const [showExportMiro, setShowExportMiro] = useState(false);

  // Selection state
  const [selectedMiroItems, setSelectedMiroItems] = useState<string[]>([]);

  // Loading states
  const [isLoading, setIsLoading] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);

  // ==========================================================================
  // Miro Connection & Operations
  // ==========================================================================

  const handleMiroConnect = async () => {
    if (!miroAccessToken) {
      onNotification({ type: 'error', title: 'Missing Token', message: 'Please enter your Miro access token' });
      return;
    }
    if (!workspaceId) {
      onNotification({
        type: 'error',
        title: 'Workspace not ready',
        message: 'Reload the page, then try connecting to Miro again.',
      });
      return;
    }

    setIsConnecting(true);
    try {
      const result = await api.connectMiro(miroAccessToken, miroTeamId || undefined);
      if (result.connected) {
        setMiroConnected(true);
        setShowMiroConfig(false);
        
        // Persist credentials to DB (and localStorage as fallback)
        try {
          const workspaceId = getCurrentWorkspaceId();
          if (workspaceId) {
            await enableWorkspaceConnector(workspaceId, 'miro');
            await saveWorkspaceConnectorCredentials(workspaceId, 'miro', {
              accessToken: miroAccessToken,
              ...(miroTeamId ? { teamId: miroTeamId } : {}),
            });
          }
          localStorage.setItem('peak_miro_token', miroAccessToken);
          if (miroTeamId) localStorage.setItem('peak_miro_team_id', miroTeamId);
        } catch (e) {
          console.warn('Could not persist Miro credentials', e);
        }
        
        onNotification({ type: 'success', title: 'Connected', message: 'Successfully connected to Miro' });

        // Load boards and templates
        const [boards, templates] = await Promise.all([
          api.listMiroBoards(),
          api.listMiroTemplates(),
        ]);
        setMiroBoards(boards);
        setMiroTemplates(templates);
        
        // Pre-select first template if available
        if (templates.length > 0) {
          setSelectedMiroTemplate(templates[0].id);
        }
      } else {
        onNotification({ type: 'error', title: 'Connection Failed', message: result.error || 'Could not connect to Miro' });
      }
    } catch (error) {
      onNotification({ type: 'error', title: 'Error', message: error instanceof Error ? error.message : 'Connection failed' });
    } finally {
      setIsConnecting(false);
    }
  };

  const handleMiroDisconnect = async () => {
    try {
      await api.disconnectMiro();
      setMiroConnected(false);
      setMiroBoards([]);
      setSelectedMiroBoard('');
      setMiroAccessToken('');
      setMiroTeamId('');
      
      // Clear from localStorage and DB
      try {
        localStorage.removeItem('peak_miro_token');
        localStorage.removeItem('peak_miro_team_id');
        const workspaceId = getCurrentWorkspaceId();
        if (workspaceId) {
          await clearConnectorCredentials(workspaceId, null, 'miro');
        }
      } catch (_e) {
        // ignore
      }
      
      onNotification({ type: 'info', title: 'Disconnected', message: 'Miro has been disconnected' });
    } catch (_error) {
      onNotification({ type: 'error', title: 'Error', message: 'Failed to disconnect from Miro' });
    }
  };

  const loadMiroPreview = async (boardId: string) => {
    setIsLoading(true);
    try {
      const preview = await api.previewMiroImport(boardId);
      setMiroPreview(preview);
      setMiroItems(preview.items.map(item => ({
        id: item.id,
        content: item.content,
        color: item.color,
        author: item.author,
        suggestedCategory: item.suggestedCategory,
      })));
      setSyncStatus(prev => ({ ...prev, pendingMiroItems: preview.totalItems }));
    } catch (_error) {
      onNotification({ type: 'error', title: 'Preview Failed', message: 'Could not load Miro board preview' });
    } finally {
      setIsLoading(false);
    }
  };

  // Template-based Miro operations
  const loadTemplateImportPreview = async (boardId: string, templateId: string) => {
    setIsLoading(true);
    try {
      const preview = await api.previewMiroTemplateImport(boardId, templateId, {
        filterToTemplateBounds: true,
        minConfidence: 'overlap',
        includeUnzoned: false,
      });
      setTemplateImportPreview(preview);
      setSyncStatus(prev => ({ ...prev, pendingMiroItems: preview.totalItems }));
    } catch (_error) {
      onNotification({ type: 'error', title: 'Preview Failed', message: 'Could not load template preview' });
    } finally {
      setIsLoading(false);
    }
  };

  const boardNameOf = (boardId: string) => miroBoards.find(b => b.id === boardId)?.name;

  /** Mapped item fields for the payload; mapped tags join the host's default tags. */
  const mappedPayloadFields = (m: ReturnType<MiroImportMapping['map']>): Record<string, unknown> => ({
    ...m.itemFields,
    ...(m.tags.length > 0 ? { tags: ['miro', 'imported', ...m.tags] } : {}),
  });

  /** What the Field Mappings set did (and anything not remembered) — its own notice beside the import result. */
  const notifyMappingSummary = (mapping: MiroImportMapping) => {
    const summary = mapping.summary();
    if (!summary) return;
    onNotification({
      type: summary.hasProblems ? 'warning' : 'info',
      title: 'Field Mappings',
      message: summary.message,
      duration: summary.hasProblems ? 9000 : 5000,
    });
  };

  const handleTemplateImport = async () => {
    if (!selectedMiroBoard || !selectedMiroTemplate) return;

    setIsLoading(true);
    try {
      const result = await api.importFromMiroTemplate(selectedMiroBoard, selectedMiroTemplate, {
        filterToTemplateBounds: true,
        minConfidence: 'overlap',
        includeUnzoned: false,
      });

      if (result.success) {
        // The Field Mappings set for Miro: fields ride the payload, attribute
        // values are written once the host has saved each item.
        const mapping = await prepareMiroImportMapping(workspaceId, { boardName: boardNameOf(selectedMiroBoard) });
        const mapped = result.items.map(item => mapping.map(item));
        // Transform items for the callback; spread toolAttributes so callers can
        // apply zone-derived metadata (altitudeLevel, itemCategory, etc.) directly.
        const importedItems: ImportItemPayload[] = result.items.map((item, i) => ({
          title: item.content.substring(0, 100),
          description: item.content.length > 100 ? item.content : undefined,
          source: 'miro',
          externalId: item.id,
          zone: item.zone?.name || 'Unassigned',
          ...mappedPayloadFields(mapped[i]),
          // Spread tool attributes so callers receive e.g. altitudeLevel, itemCategory
          // (template zones win over the set where both set the same field)
          ...(item.toolAttributes ?? {}),
        }));

        const outcome = await onImportItems(importedItems);
        await mapping.finishSaved(mapped, outcome.savedIds);
        setShowImportMiro(false);
        setSyncStatus(prev => ({ ...prev, lastMiroSync: new Date(), pendingMiroItems: 0 }));

        // Zone summary only when every sticky saved — it describes the board.
        const zoneSummary = result.zoneSummary
          .filter(z => z.itemCount > 0)
          .map(z => `${z.zoneName}: ${z.itemCount}`)
          .join(', ');

        onNotification(
          summarizeImportOutcome({
            ...outcome,
            label: 'Miro import',
            successTitle: 'Miro zone import complete',
            note: zoneSummary || undefined,
          }),
        );
        notifyMappingSummary(mapping);
      } else {
        onNotification({
          type: 'error',
          title: 'Import Failed',
          message: result.warnings?.join(', ') || 'Unknown error',
        });
      }
    } catch (error) {
      onNotification({ type: 'error', title: 'Error', message: error instanceof Error ? error.message : 'Import failed' });
    } finally {
      setIsLoading(false);
    }
  };

  const loadTemplateExportPreview = async (templateId: string) => {
    setIsLoading(true);
    try {
      const preview = await api.previewMiroTemplateExport(
        templateId,
        priorities.map(p => ({
          id: p.id,
          title: p.title,
          description: p.description != null ? String(p.description) : undefined,
          attributes: {
            rank: p.rank,
            score: p.score,
            category: p.category,
          },
        }))
      );
      setTemplateExportPreview(preview);
    } catch (_error) {
      onNotification({ type: 'error', title: 'Preview Failed', message: 'Could not generate export preview' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleTemplateExport = async () => {
    if (!selectedMiroTemplate || priorities.length === 0) return;

    setIsLoading(true);
    try {
      // The Miro export set, run backwards: an item's value picks its sticky color.
      const { colors } = await miroStickyColorsForExport(workspaceId, priorities);
      const result = await api.exportToMiroTemplate(
        selectedMiroTemplate,
        priorities.map(p => ({
          id: p.id,
          title: p.title,
          description: p.description != null ? String(p.description) : undefined,
          attributes: {
            rank: p.rank,
            score: p.score,
            category: p.category,
          },
          ...(colors.has(p.id) ? { color: colors.get(p.id) } : {}),
        })),
        {
          boardId: selectedMiroBoard || undefined,
          createBoard: !selectedMiroBoard,
          newBoardName: `${listName} - ${new Date().toLocaleDateString()}`,
        }
      );

      if (result.success) {
        setShowExportMiro(false);
        setSyncStatus(prev => ({ ...prev, lastMiroSync: new Date() }));
        onNotification({
          type: 'success',
          title: 'Export Complete',
          message: `Created ${result.itemsCreated} items in Miro${colors.size > 0 ? ` (${colors.size} sticky color${colors.size !== 1 ? 's' : ''} from your Field Mappings set)` : ''}${result.boardUrl ? '. Opening board...' : ''}`,
        });

        // Open the Miro board in a new tab
        if (result.boardUrl) {
          window.open(result.boardUrl, '_blank');
        }
      } else {
        onNotification({
          type: 'error',
          title: 'Export Failed',
          message: result.warnings?.join(', ') || 'Unknown error',
        });
      }
    } catch (error) {
      onNotification({ type: 'error', title: 'Error', message: error instanceof Error ? error.message : 'Export failed' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleMiroImport = async () => {
    if (selectedMiroItems.length === 0 || !selectedMiroBoard) return;

    setIsLoading(true);
    try {
      const result = await api.importFromMiro(selectedMiroBoard, {
        targetSessionId: listId,
      });

      if (result.success) {
        // Filter to only the stickies the user checked, then apply color rules.
        // result.items is `any[]` at the source (lib/integrationsApi.ts —
        // Miro's raw sticky-note payload isn't fully modeled); narrow to the
        // fields this block actually reads instead of propagating `any`.
        interface RawMiroItem {
          id?: string;
          externalId?: string;
          label?: string;
          title?: string;
          description?: string;
          attributes?: { color?: string };
          color?: string;
        }
        const allItems: RawMiroItem[] = result.items ?? [];
        const filteredItems = allItems.filter((item) => {
          const id = item.id || '';
          const extId = item.externalId || '';
          return selectedMiroItems.includes(id) || selectedMiroItems.includes(extId);
        });

        const mapping = await prepareMiroImportMapping(workspaceId, { boardName: boardNameOf(selectedMiroBoard) });
        const mapped = filteredItems.map((item) => mapping.map(item));
        const importedItems: ImportItemPayload[] = filteredItems.map((item, i) => {
          const base: ImportItemPayload = {
            title: item.label || item.title || 'Untitled',
            description: item.description,
            source: 'miro',
            externalId: item.externalId || item.id,
            ...mappedPayloadFields(mapped[i]),
          };

          // Apply configured color → field: value rules (items entity type)
          if (itemColorRules.length > 0) {
            const noteColor = item.attributes?.color || item.color || '';
            const rule = itemColorRules.find(r =>
              r.color.toLowerCase().trim() === noteColor.toLowerCase().trim()
            );
            if (rule?.field && rule.value !== undefined && rule.value !== '') {
              // Coerce to number for numeric fields; keep string otherwise
              const coerced = rule.field === 'altitudeLevel'
                ? parseInt(rule.value, 10) || rule.value
                : rule.value;
              base[rule.field] = coerced;
            }
          }

          return base;
        });

        const outcome = await onImportItems(importedItems);
        await mapping.finishSaved(mapped, outcome.savedIds);
        onNotification(
          summarizeImportOutcome({ ...outcome, label: 'Miro import', successTitle: 'Imported from Miro' }),
        );
        notifyMappingSummary(mapping);

        setSyncStatus(prev => ({
          ...prev,
          lastMiroSync: new Date(),
          linkedItems: prev.linkedItems + outcome.created,
        }));

        setShowImportMiro(false);
        setSelectedMiroItems([]);
      } else {
        onNotification({
          type: 'error',
          title: 'Import Failed',
          message: `Failed to import: ${result.errors.join(', ')}`,
        });
      }
    } catch (error) {
      onNotification({
        type: 'error',
        title: 'Import Error',
        message: error instanceof Error ? error.message : 'Failed to import from Miro',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const toggleMiroSelection = (id: string) => {
    setSelectedMiroItems(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const selectAllMiroItems = () => {
    setSelectedMiroItems(miroItems.map(m => m.id));
  };

  return {
    miroConnected,
    setMiroConnected,
    showMiroConfig,
    setShowMiroConfig,
    miroAccessToken,
    setMiroAccessToken,
    miroTeamId,
    setMiroTeamId,
    miroBoards,
    setMiroBoards,
    selectedMiroBoard,
    setSelectedMiroBoard,
    miroItems,
    miroTemplates,
    setMiroTemplates,
    selectedMiroTemplate,
    setSelectedMiroTemplate,
    useTemplateMode,
    setUseTemplateMode,
    templateImportPreview,
    templateExportPreview,
    showImportMiro,
    setShowImportMiro,
    showExportMiro,
    setShowExportMiro,
    selectedMiroItems,
    isLoading,
    isConnecting,
    handleMiroConnect,
    handleMiroDisconnect,
    loadMiroPreview,
    loadTemplateImportPreview,
    handleTemplateImport,
    loadTemplateExportPreview,
    handleTemplateExport,
    handleMiroImport,
    toggleMiroSelection,
    selectAllMiroItems,
  };
}

export type MiroConnection = ReturnType<typeof useMiroConnection>;
